/* eslint-disable */
import Bugsnag from '@bugsnag/browser';
import BugsnagPerformance from '@bugsnag/browser-performance';
import { VueRouterRoutingProvider } from '@bugsnag/vue-router-performance';
import { createErrorRateLimiter } from './bugsnagRateLimit';

/**
 * Bugsnag Error Tracking & Performance Monitoring Module
 * Handles error reporting and performance tracking for the browser
 */

// Vue Router integration for BugsnagPerformance
let vueRouterInstance = null;
let vueRouterBugsnagHooksAttached = false;
let bugsnagErrorsInitialized = false;
let bugsnagPerformanceInitialized = false;

// Parses a JS Error stack string into Bugsnag stacktrace frame objects
function parseStack(stack) {
  return (stack || '').split('\n').slice(1).map(line => {
    const m = line.trim().match(/^at\s+(?:([\w$.<>[\] ]+?)\s+\()?(.+):(\d+):(\d+)\)?$/);
    if (!m) return null;
    return { method: m[1] || '?', file: m[2], lineNumber: +m[3], columnNumber: +m[4] };
  }).filter(Boolean);
}

// Client-side rate limiting: drop an event if it exceeds EITHER cap for its group —
// max-per-HOUR or max-per-DAY (both enforced; values come from config). Buckets use the
// same group Bugsnag itself uses (groupingHash), so e.g. all network errors count together.
// Logic lives in ./bugsnagRateLimit (pure + unit-tested). One shared limiter across both
// init paths so counters are global, exactly as the old module-level Maps were.
const errorRateLimiter = createErrorRateLimiter();

function verboseLog(msg, data) {
  if (typeof window !== 'undefined' && window.GT && window.GT.bugsnag && window.GT.bugsnag.client && window.GT.bugsnag.client.verbose) {
    console.log('[Bugsnag] ' + msg, data !== undefined ? data : '');
  }
}

function findEnrichmentSource(err) {
  let cur = err;
  const visited = new Set();
  while (cur && typeof cur === 'object') {
    if (visited.has(cur)) break;
    visited.add(cur);
    if (cur._meta && typeof cur._meta === 'object') return cur;
    cur = cur.cause;
  }
  return null;
}

const BUGSNAG_META_MAX_STRING = 8000;
const BUGSNAG_META_MAX_DEPTH = 8;
const BUGSNAG_META_MAX_ARRAY = 40;

function cloneValueForMetadata(value, depth, seen) {
  if (depth > BUGSNAG_META_MAX_DEPTH) return '[Truncated depth]';
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') {
    return value.length > BUGSNAG_META_MAX_STRING
      ? value.slice(0, BUGSNAG_META_MAX_STRING) + '\u2026'
      : value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'bigint') return String(value);
  if (typeof value === 'function' || typeof value === 'symbol') return undefined;
  if (value instanceof Error) {
    return { name: value.name, message: value.message };
  }
  if (Array.isArray(value)) {
    const out = [];
    const n = Math.min(value.length, BUGSNAG_META_MAX_ARRAY);
    for (let i = 0; i < n; i++) {
      const v = cloneValueForMetadata(value[i], depth + 1, seen);
      if (v !== undefined) out.push(v);
    }
    if (value.length > BUGSNAG_META_MAX_ARRAY) {
      out.push('[' + (value.length - BUGSNAG_META_MAX_ARRAY) + ' more items]');
    }
    return out;
  }
  if (typeof value === 'object') {
    if (seen.has(value)) return '[Circular]';
    seen.add(value);
    const out = {};
    for (const k of Object.keys(value)) {
      const v = cloneValueForMetadata(value[k], depth + 1, seen);
      if (v !== undefined) out[k] = v;
    }
    seen.delete(value);
    return out;
  }
  return String(value);
}

function cloneSectionForMetadata(data) {
  if (data == null || typeof data !== 'object' || Array.isArray(data)) return data;
  return cloneValueForMetadata(data, 0, new WeakSet());
}

function applyStoreApiRequestToEvent(event, apiRequest) {
  if (!apiRequest || typeof apiRequest !== 'object') return;
  if (apiRequest.path) {
    event.request.url = apiRequest.path;
  } else {
    const base = apiRequest.baseURL || '';
    const path = apiRequest.url || '';
    let full = '';
    if (base && path) {
      const b = base.endsWith('/') ? base.slice(0, -1) : base;
      const p = path.startsWith('/') ? path : `/${path}`;
      full = b + p;
    } else {
      full = path || base;
    }
    if (full) event.request.url = full;
  }
  if (apiRequest.method) event.request.httpMethod = String(apiRequest.method).toUpperCase();
}

function appendVueRouteToEvent(event) {
  const r = vueRouterInstance?.currentRoute?.value;
  if (!r) return;
  event.addMetadata('view', cloneSectionForMetadata({
    name: r.name,
    path: r.path,
    fullPath: r.fullPath,
    params: r.params,
    query: r.query,
  }));
}

function attachVueRouterBugsnagNavigation() {
  if (!bugsnagErrorsInitialized || !vueRouterInstance || vueRouterBugsnagHooksAttached) {
    return;
  }
  vueRouterBugsnagHooksAttached = true;
  vueRouterInstance.afterEach((to) => {
    const label = to.name != null ? String(to.name) : to.fullPath;
    const ctx = to.name != null ? `${String(to.name)} ${to.fullPath}` : to.fullPath;
    Bugsnag.setContext(ctx);
    Bugsnag.leaveBreadcrumb(
      label,
      cloneSectionForMetadata({
        fullPath: to.fullPath,
        name: to.name,
        params: to.params,
        query: to.query,
      }),
      'navigation',
    );
  });
}

function addBugsnagAppMetadata(GT) {
  if (!GT) return;
  const appMeta = {
    env: GT.env,
    buildVersion: GT.buildVersion,
  };
  if (typeof document !== 'undefined' && document.documentElement?.lang) {
    appMeta.docLocale = document.documentElement.lang;
  }
  if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
    appMeta.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
  if (typeof window !== 'undefined' && window.devicePixelRatio) {
    appMeta.devicePixelRatio = window.devicePixelRatio;
  }
  if (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) {
    appMeta.hardwareConcurrency = navigator.hardwareConcurrency;
  }
  Bugsnag.addMetadata('app', cloneSectionForMetadata(appMeta));
}

// Chromium fires this as a synthetic window error when ResizeObserver nested notifications
// are deferred. Not an app crash — name it so Bugsnag groups/reads clearly.
function getResizeObserverLoopErrorName(event) {
  const msg = event?.errors?.[0]?.errorMessage
    || event?.originalError?.message
    || '';
  if (typeof msg === 'string' && msg.includes('ResizeObserver loop')) {
    return 'RESIZE_OBSERVER_LOOP';
  }
  return null;
}

// Unhandled Worker errors while importScripts loads moment/lodash/dhtmlx
// (Chrome NetworkError / Firefox NS_BINDING_ABORTED). Asset is usually fine (HTTP 200);
// client aborted or lost the request. BugSnag group: 69b2d444e8d635433d668145.
//
// How to fix (not done here — naming only):
// 1. Lazy-create Worker on first calculate(), not in singleton constructors.
// 2. Attach worker.onerror at creation time (createWorker.js now waits until calculate).
// 3. On load failure: terminate, recreate Worker, reject the pending Promise (no throw).
// 4. reportWorkerError should notifyErrorSafely + reject, not throw.
function getWorkerImportScriptsErrorName(event) {
  const msg = event?.errors?.[0]?.errorMessage
    || event?.originalError?.message
    || '';
  if (typeof msg !== 'string' || !msg) return null;

  const frameFile = event?.errors?.[0]?.stacktrace?.[0]?.file || '';
  const isWorkerStack = /\/workers\/[^/?]+\.js/i.test(frameFile);
  const isImportScriptsFail = /importScripts/i.test(msg)
    || /NS_BINDING_ABORTED/i.test(msg);

  if (isWorkerStack && isImportScriptsFail) {
    return 'WORKER_IMPORTSCRIPTS_LOAD_FAILED';
  }
  return null;
}

function createBugsnagOnErrorHandler(maxPerGroupPerHour, maxPerGroupPerDay, verboseErrors) {
  return (event) => {
    const original = event.originalError;
    const enriched = findEnrichmentSource(original) || (original && original._meta ? original : null);
    const errorName = enriched?._errorName
      || original?._errorName
      || getResizeObserverLoopErrorName(event)
      || getWorkerImportScriptsErrorName(event);
    if (errorName) {
      if (event.errors?.[0]) {
        event.errors[0].errorClass = errorName;
      }
      event.groupingHash = errorName;
    }
    if (enriched && enriched._meta && typeof enriched._meta === 'object') {
      const errorClass = errorName || 'Error';
      const errorMsg = enriched._capturedMessage || enriched.message || original?.message || '';
      const ridLog = enriched._meta?.APIREQUEST?.requestId;
      const ridSuffix = ridLog != null && String(ridLog).length > 0 ? ` | reqId=${ridLog}` : '';
      // Naming buckets stay distinct; severity stays error for all of them.
      const isClientNetworkFailure = typeof errorName === 'string'
        && errorName.startsWith('STORE_API_ERROR_NETWORK_');
      const isGatewayFailure = errorName === 'STORE_API_ERROR_GATEWAY';
      if (isClientNetworkFailure) {
        const offlineHint = enriched._meta.APIRESPONSE?.networkFailureReason
          || (enriched._meta.APIRESPONSE?.navigatorOnLine === false ? 'browser offline' : 'no HTTP response');
        console.error(`[Bugsnag] Internet connectivity issue on the client (${offlineHint}) | ${errorClass} | ${errorMsg}${ridSuffix}`, enriched._meta);
      } else if (isGatewayFailure) {
        const gatewayHint = enriched._meta.APIRESPONSE?.gatewayFailureReason
          || 'edge/gateway HTML 5xx';
        console.error(`[Bugsnag] Gateway/maintenance response (${gatewayHint}) | ${errorClass} | ${errorMsg}${ridSuffix}`, enriched._meta);
      } else {
        console.error(`[Bugsnag] ${errorClass} | ${errorMsg}${ridSuffix}`, enriched._meta);
      }
      if (event.errors?.[0]) {
        if (enriched._capturedMessage) event.errors[0].errorMessage = enriched._capturedMessage;
        if (enriched._capturedStack) {
          const frames = parseStack(enriched._capturedStack);
          if (frames.length) event.errors[0].stacktrace = frames;
        }
      }
      for (const [section, data] of Object.entries(enriched._meta)) {
        if (data != null && typeof data === 'object' && !Array.isArray(data)) {
          event.addMetadata(section, cloneSectionForMetadata(data));
        }
      }
      applyStoreApiRequestToEvent(event, enriched._meta.APIREQUEST);
    }
    const requestIdForEvent = enriched?._meta?.APIREQUEST?.requestId
      ?? original?.config?.meta?.requestId;
    if (requestIdForEvent != null && String(requestIdForEvent).length > 0) {
      event.addMetadata('requestId', { id: String(requestIdForEvent) });
    }
    appendVueRouteToEvent(event);
    if (errorRateLimiter.shouldDiscard(event, maxPerGroupPerHour, maxPerGroupPerDay)) {
      return false;
    }
    if (verboseErrors) {
      const e = event.errors && event.errors[0];
      const name = e ? (e.errorClass || 'Error') : '?';
      const msg = e ? (e.errorMessage || '') : '?';
      console.log('[Bugsnag] error sent | ' + name + ' | ' + msg);
    }
    return true;
  };
}

// Returns span duration in ms, or null if unknown. Handles OTel HrTime [sec, nsec], number (ms), and our custom perf.duration_sec attribute.
function getSpanDurationMs(span) {
  if (!span) return null;
  let secFromAttr = span.attributes?.['perf.duration_sec'];
  if (secFromAttr == null && span.attributes?.get) secFromAttr = span.attributes.get('perf.duration_sec');
  if (secFromAttr == null && typeof span.getAttribute === 'function') secFromAttr = span.getAttribute('perf.duration_sec');
  if (secFromAttr == null && Array.isArray(span.attributes)) {
    const entry = span.attributes.find(a => a && (a.key === 'perf.duration_sec' || a.name === 'perf.duration_sec'));
    if (entry && entry.value != null) secFromAttr = entry.value.doubleValue ?? entry.value.intValue ?? entry.value;
  }
  if (typeof secFromAttr === 'number' && secFromAttr >= 0) return Math.round(secFromAttr * 1000);
  const toMs = (t) => {
    if (t == null) return null;
    if (typeof t === 'number') return t;
    if (Array.isArray(t) && t.length >= 2) return t[0] * 1000 + t[1] / 1e6;
    return null;
  };
  if (span.duration != null) {
    const d = toMs(span.duration);
    if (d != null && d >= 0) return d;
  }
  const start = span.startTime;
  const end = span.endTime;
  if (start == null || end == null) return null;
  const startMs = toMs(start);
  const endMs = toMs(end);
  if (startMs == null || endMs == null) return null;
  const durationMs = endMs - startMs;
  if (durationMs < 0) return null;
  return durationMs;
}

/**
 * Default settings for BugsnagPerformance browser instrumentation
 */
const BUGSNAG_PERFORMANCE_DEFAULTS = {
  autoInstrumentFullPageLoads: true,
  autoInstrumentNetworkRequests: true,
  autoInstrumentRouteChanges: true,
  generateAnonymousId: false,
  maximumBatchSize: 100,
};

/**
 * Set Vue Router instance for performance tracking
 */
export function setRouter(router) {
  vueRouterInstance = router;
}

/**
 * Initialize Bugsnag error reporting and performance monitoring
 */
export function initBugsnag(debug = false) {
  try {
    const GT = typeof window !== 'undefined' ? window.GT : undefined;
    // Initialize Bugsnag Errors (config from server; no need for window.Bugsnag before start)
    if (GT && GT.bugsnag && GT.bugsnag.client && GT.bugsnag.client.bugsnagApiKey &&
        GT.bugsnag.client.errors && GT.bugsnag.client.errors.enabled) {
      const maxPerGroupPerHour = GT.bugsnag.client.errors.maxEventsPerGroupPerHour;
      const maxPerGroupPerDay = GT.bugsnag.client.errors.maxEventsPerGroupPerDay;
      const verboseErrors = Boolean(GT.bugsnag.client.verbose);
      const bugSnagClientConfig = {
        apiKey: GT.bugsnag.client.bugsnagApiKey,
        releaseStage: GT.env,
        autoTrackSessions: false,
        sendCode: false,
        appVersion: GT.buildVersion,
        verbose: Boolean(GT.bugsnag.client.verbose),
        autoDetectErrors: true,
        enabledErrorTypes: { unhandledExceptions: true, unhandledRejections: true },
        maxBreadcrumbs: 40,
        onError: createBugsnagOnErrorHandler(maxPerGroupPerHour, maxPerGroupPerDay, verboseErrors),
      };
      if (GT.bugsnag.client.enabledReleaseStages && GT.bugsnag.client.enabledReleaseStages.length) {
        bugSnagClientConfig.enabledReleaseStages = GT.bugsnag.client.enabledReleaseStages;
      }

      Bugsnag.start(bugSnagClientConfig);
      bugsnagErrorsInitialized = true;
      verboseLog('errors initialized');
      drainLocaleFallbackNotify();

      if (debug && window.GT) {
        Bugsnag.addMetadata(window.GT);
      }
    }

    // Set Bugsnag user info and metadata
    if (bugsnagErrorsInitialized && window.user && window.user.id) {
      let userName = window.user.email;
      if (window.user.firstName) {
        userName = window.user.firstName + (window.user.lastName ? ' ' + window.user.lastName : '');
      }

      Bugsnag.setUser(
        String(window.user.id),
        window.user.email,
        userName
      );

      // User metadata
      Bugsnag.addMetadata('user', {
        userId: window.user.id,
        email: window.user.email,
        locale: window.user.locale,
        loginMethod: window.user.loginMethod,
        registrationTime: window.user.registrationTime,
      });

      // Team metadata
      if (window.user.team) {
        Bugsnag.addMetadata('team', {
          teamId: window.user.team.id,
          isOwner: window.user.team.is_owner,
          ownerId: window.user.team.owner_id,
          ownerEmail: window.user.team.owner_email,
          isJira: window.user.team.is_jira,
        });
      }

      // Subscription metadata
      if (window.user.subscription) {
        Bugsnag.addMetadata('subscription', {
          isTrial: window.user.subscription.is_trial,
          isAnnual: window.user.subscription.is_annual,
          planId: window.user.subscription.payment_plans_id,
          provider: window.user.subscription.provider,
          userCount: window.user.subscription.user_count,
          dateTo: window.user.subscription.date_to,
        });
      }

      // SSO metadata
      if (window.user.sso) {
        Bugsnag.addMetadata('sso', {
          enabled: window.user.sso.enabled,
        });
      }

      // 2FA metadata
      if (window.user.twoFa) {
        Bugsnag.addMetadata('twoFa', {
          active: window.user.twoFa.active,
        });
      }
    }

    if (bugsnagErrorsInitialized && GT) {
      addBugsnagAppMetadata(GT);
      attachVueRouterBugsnagNavigation();
    }

    // Bugsnag automatically collects: host, pathname, userAgent, screen size, etc.
    // appVersion and releaseStage are already set in config above

    // Bugsnag Performance: same apiKey as Errors; appVersion + releaseStage for dashboard filters (web perf guide)
    const perfEnabled = GT && GT.bugsnag && GT.bugsnag.client && GT.bugsnag.client.performance && GT.bugsnag.client.performance.enabled;
    if (perfEnabled) {
      const perfConfig = GT.bugsnag.client.performance;
      const minAutomaticMs = perfConfig.minAutomaticRequestDurationMs ?? 3000;
      const minCustomMs = perfConfig.minCustomSpanDurationMs ?? 1000;

      const samplingProbability = perfConfig.samplingProbability != null
        ? perfConfig.samplingProbability
        : (GT.bugsnag.client.verbose ? 1.0 : undefined);
      const verbosePerf = Boolean(GT.bugsnag.client.verbose);
      const performanceConfig = {
        apiKey: GT.bugsnag.client.bugsnagApiKey,
        appVersion: GT.buildVersion,
        releaseStage: GT.env,
        autoInstrumentFullPageLoads: BUGSNAG_PERFORMANCE_DEFAULTS.autoInstrumentFullPageLoads,
        autoInstrumentNetworkRequests: BUGSNAG_PERFORMANCE_DEFAULTS.autoInstrumentNetworkRequests,
        autoInstrumentRouteChanges: BUGSNAG_PERFORMANCE_DEFAULTS.autoInstrumentRouteChanges,
        generateAnonymousId: BUGSNAG_PERFORMANCE_DEFAULTS.generateAnonymousId,
        maximumBatchSize: (verbosePerf || samplingProbability === 1) ? 10 : (perfConfig.maximumBatchSize ?? BUGSNAG_PERFORMANCE_DEFAULTS.maximumBatchSize),
      };
      if (verbosePerf) {
        performanceConfig.logger = {
          debug: (msg) => console.log('[Bugsnag Perf]', msg),
          info: (msg) => console.log('[Bugsnag Perf]', msg),
          warn: (msg) => console.warn('[Bugsnag Perf]', msg),
          error: (msg) => console.error('[Bugsnag Perf]', msg),
        };
      }
      if (samplingProbability != null) {
        performanceConfig.samplingProbability = samplingProbability;
      }
      if (GT.bugsnag.client.enabledReleaseStages && GT.bugsnag.client.enabledReleaseStages.length) {
        performanceConfig.enabledReleaseStages = GT.bugsnag.client.enabledReleaseStages;
      }
      if (bugsnagErrorsInitialized) {
        performanceConfig.bugsnag = Bugsnag;
      }

      const onSpanEndFn = (span) => {
        try {
          const durationMs = getSpanDurationMs(span);
          const name = (span && (span.name || (span.spanContext && span.spanContext.name))) || 'span';
          const isCustomSpan = typeof name === 'string' && name.length > 0 && name[0] !== '[';
          if (isCustomSpan) {
            const sendCustom = durationMs === null || durationMs >= minCustomMs;
            if (verbosePerf) {
              const action = sendCustom ? 'sent' : 'dropped (< ' + minCustomMs + 'ms)';
              const dur = durationMs != null ? ' | durationMs=' + durationMs : '';
              console.log('[Bugsnag] perf ' + action + ' | ' + name + dur);
            }
            if (durationMs !== null && durationMs < minCustomMs) return false;
            return true;
          }
          const send = durationMs === null || durationMs >= minAutomaticMs;
          if (durationMs !== null && durationMs < minAutomaticMs) {
            return false;
          }
          return true;
        } catch (e) {
          console.error('[Bugsnag] onSpanEnd error:', e);
          return true;
        }
      };
      performanceConfig.onSpanEnd = [onSpanEndFn];

      // Add Vue Router integration if available
      if (vueRouterInstance) {
        performanceConfig.routingProvider = new VueRouterRoutingProvider(vueRouterInstance);
      }

      verboseLog('performance: batch size ' + performanceConfig.maximumBatchSize + ' (smaller = send sooner); watch for [Bugsnag Perf] logs and Network requests to bugsnag.com');

      BugsnagPerformance.start(performanceConfig);
      bugsnagPerformanceInitialized = true;
      verboseLog('performance initialized | auto>=' + minAutomaticMs + 'ms custom>=' + minCustomMs + 'ms | samplingProbability=' + (samplingProbability != null ? samplingProbability : 'default'));
      verboseLog('performance: check Network tab for requests to bugsnag.com; ensure dashboard release stage includes this env');
      if (typeof window !== 'undefined' && GT.bugsnag.client.verbose) {
        window.testBugsnagPerfSpan = testPerfSpan;
      }

      if (typeof window !== 'undefined') {
        const flushPerf = () => {
          if (typeof BugsnagPerformance.flush === 'function') {
            BugsnagPerformance.flush();
            if (verbosePerf) console.log('[Bugsnag] perf flush called');
          }
        };
        window.addEventListener('pagehide', flushPerf);
        window.addEventListener('beforeunload', flushPerf);
        if (verbosePerf && typeof BugsnagPerformance.flush === 'function') {
          setInterval(flushPerf, 15000);
        }
      }
    }

  } catch (error) {
    console.error('[Bugsnag] Initialization failed:', error);
  }
}

export function isBugsnagInitialized() {
  return bugsnagErrorsInitialized;
}

export function isBugsnagPerformanceInitialized() {
  return bugsnagPerformanceInitialized;
}

// Custom spans: https://docs.bugsnag.com/performance/integration-guides/web/#sending-custom-spans
export function startPerfSpan(name) {
  if (!bugsnagPerformanceInitialized) return null;
  try {
    const span = BugsnagPerformance.startSpan(name);
    verboseLog('perf span started | ' + name);
    return span;
  } catch (e) {
    return null;
  }
}

export function verboseLogPerfSpanEnd(name, durationMs) {
  verboseLog('perf span ended | ' + name + ' | durationMs=' + durationMs);
}

// Test helper: creates a custom span "testCustomSpan" lasting ~1.5s. Call from console: window.testBugsnagPerfSpan() or import and testPerfSpan().
export function testPerfSpan(durationSec = 1.5) {
  if (!bugsnagPerformanceInitialized) {
    console.warn('[Bugsnag] testPerfSpan: Performance not initialized');
    return;
  }
  const name = 'testCustomSpan';
  const span = startPerfSpan(name);
  if (!span) return;
  setTimeout(() => {
    const sec = durationSec;
    if (span.setAttribute) span.setAttribute('perf.duration_sec', sec);
    verboseLogPerfSpanEnd(name, Math.round(sec * 1000));
    if (span.end) span.end();
    console.log('[Bugsnag] test span ended, durationMs=' + Math.round(sec * 1000));
  }, durationSec * 1000);
}

// Drains window.__localeFallbackNotify — events queued by loadLocales.js before Bugsnag was ready.
// Called immediately after Bugsnag.start so no locale failure is lost.
function drainLocaleFallbackNotify() {
  const pending = window.__localeFallbackNotify;

  if (!Array.isArray(pending) || !pending.length) return;

  window.__localeFallbackNotify = [];

  for (const item of pending) {
    const err = new Error(item.message);

    err.name = item.errorClass;
    err._errorName = item.errorClass;
    err._meta = { locales: item.meta };

    try {
      Bugsnag.notify(err, event => {
        event.errors[0].errorClass = item.errorClass;
        event.addMetadata('locales', cloneSectionForMetadata(item.meta));
      });
    } catch (e) {
      console.error('[Bugsnag] drainLocaleFallbackNotify failed:', e);
    }
  }
}

// Safe notify: preserves original stack, does not throw. Used by log.error().
// metadata: { sectionName: { key: value } } - each section becomes a tab in Bugsnag
// errorClass: sets the error class in Bugsnag (the main title/grouping)
export function notifyErrorSafely(error, metadata = {}, errorClass = '') {
  const err = error instanceof Error ? error : new Error(String(error));

  if (!bugsnagErrorsInitialized && typeof window !== 'undefined') {
    const GT = window.GT;
    if (GT && GT.bugsnag && GT.bugsnag.client && GT.bugsnag.client.bugsnagApiKey &&
        GT.bugsnag.client.errors && GT.bugsnag.client.errors.enabled) {
      const lazyMaxPerHour = GT.bugsnag.client.errors.maxEventsPerGroupPerHour;
      const lazyMaxPerDay = GT.bugsnag.client.errors.maxEventsPerGroupPerDay;
      const lazyVerbose = Boolean(GT.bugsnag.client.verbose);
      const lazyConfig = {
        apiKey: GT.bugsnag.client.bugsnagApiKey,
        releaseStage: GT.env,
        autoTrackSessions: false,
        sendCode: false,
        appVersion: GT.buildVersion,
        verbose: lazyVerbose,
        autoDetectErrors: true,
        enabledErrorTypes: { unhandledExceptions: true, unhandledRejections: true },
        maxBreadcrumbs: 40,
        onError: createBugsnagOnErrorHandler(lazyMaxPerHour, lazyMaxPerDay, lazyVerbose),
      };
      if (GT.bugsnag.client.enabledReleaseStages && GT.bugsnag.client.enabledReleaseStages.length) {
        lazyConfig.enabledReleaseStages = GT.bugsnag.client.enabledReleaseStages;
      }
      Bugsnag.start(lazyConfig);
      bugsnagErrorsInitialized = true;
      addBugsnagAppMetadata(GT);
      attachVueRouterBugsnagNavigation();
    }
  }

  if (!bugsnagErrorsInitialized) return;

  // Set error.name so Bugsnag picks it up as errorClass before constructing the event
  if (errorClass) {
    err.name = errorClass;
  }

  verboseLog('reporting error', { message: err.message, workerSource: err.workerSource || null });

  try {
    Bugsnag.notify(err, (event) => {
      if (errorClass) {
        event.errors[0].errorClass = errorClass;
        event.groupingHash = errorClass;
      }
      if (err.workerSource) {
        event.addMetadata('worker', { source: err.workerSource });
      }
      const nRid = err.config?.meta?.requestId;
      if (nRid != null && String(nRid).length > 0) {
        event.addMetadata('requestId', { id: String(nRid) });
      }
    });
  } catch (e) {
    console.error('[Bugsnag] notify failed:', e);
  }
}
